import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import { Alert, Box, Button, CircularProgress, Typography } from '@mui/material';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import GoogleWatchdogService from 'services/GoogleWatchdogService';
import useKeymap from 'shared/hooks/useKeymap';
import { ACTION_STEP_STORES, ActionItem, CaseRecord, CardBrief, CardChild, CardIssue, ChangeRow, CoverageOutcome, HeroMetric, IssueTable, SEVERITY_COLORS, WatchdogCheck, WatchdogCheckDefinition, WatchdogCoverage } from './types';
import { itemCache } from './cache';
import { itemCurrency, localDay, parseTs, WEEKS_PER_MONTH } from './logic';
import { DismissDialog, ResolveDialog, SnoozeDialog } from './components/ActionDialogs';
import DetailHeader from './components/DetailHeader';
import FactStrip from './components/FactStrip';
import FactsRail from './components/FactsRail';
import FindingText from './components/FindingText';
import TrendCharts from './components/TrendCharts';
import Waterfall from './components/Waterfall';
import CaseHistory from './components/CaseHistory';
import { formatImpact } from './components/CardFaceSlots';
import ActionSteps from './components/ActionSteps';

const service = new GoogleWatchdogService();

// The explainer deck, served from this app's own public/ directory. Its loader resolves a
// slide id in the hash, so deckHref('s-cl-c2s') opens that specialist's checklist rather
// than the title slide. Without an id the link opens the deck at the beginning.
const DECK_URL = '/watchdog-deck.html';
function deckHref(slide?: string): string {
  return slide ? `${DECK_URL}#/${slide}` : DECK_URL;
}

/**
 * The card reads in three moves, and these are their headings: how the campaign is doing,
 * what we found, what we checked. Before them the same material ran together as one column
 * of prose and panels, so the reader had to work out which part answered which question
 * (L: "the data is just jumbled, placed in weird places, and not organized well").
 */
function SectionHeading({ children, count }: { children: React.ReactNode; count?: number }) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, mt: 4, mb: 1.5, borderBottom: '1px solid #E2E8F0', pb: 0.75 }}>
      <Typography sx={{ fontSize: '0.8125rem', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'text.secondary' }}>
        {children}
      </Typography>
      {count != null && (
        <Typography sx={{ fontSize: '0.8125rem', fontWeight: 700, color: 'text.secondary', fontVariantNumeric: 'tabular-nums' }}>{count}</Typography>
      )}
    </Box>
  );
}

/**
 * actionSteps: the triage route's version of the card (/google-watchdog/triage/items/:id), which
 * adds the agent's action steps for stores that have them. The main page's card is unchanged.
 */
export default function ActionItemDetail({ actionSteps = false }: { actionSteps?: boolean }) {
  const { id: rawId } = useParams<{ id: string }>();
  const id = decodeURIComponent(rawId);
  const history = useHistory();
  const [item, setItem] = useState<ActionItem | null>(() => itemCache.get(id) ?? null);
  const [error, setError] = useState('');
  const [dialog, setDialog] = useState<'resolve' | 'dismiss' | 'snooze' | null>(null);
  // An action step's Approve or Reject dialog is open (it owns the keyboard, like the dialogs above).
  const [stepDialogOpen, setStepDialogOpen] = useState(false);
  const [toast, setToast] = useState('');
  const [changes, setChanges] = useState<ChangeRow[]>([]);
  const [coverage, setCoverage] = useState<WatchdogCoverage | null | undefined>(undefined);
  const [caseRecord, setCaseRecord] = useState<CaseRecord | null | undefined>(undefined);

  useEffect(() => { service.itemChanges(id).then(setChanges); }, [id]);
  useEffect(() => { service.getCoverage(id).then(setCoverage); }, [id]);
  useEffect(() => { setCaseRecord(undefined); }, [id]);

  const load = useCallback(async () => {
    try {
      service.getCase(id).then(setCaseRecord);
      const fresh = await service.getItem(id);
      itemCache.set(id, fresh);
      setItem(fresh);
    } catch (e: any) {
      setError(e.message);
    }
  }, [id]);
  useEffect(() => { load(); }, [load]);

  // A card opened from the triage page carries where it came from, so Esc returns there.
  // The queue page pushes a plain path and gets the default.
  const { state: navState } = useLocation<{ from?: string } | undefined>();
  const back = useCallback(() => history.push(navState?.from ?? (actionSteps ? '/google-watchdog/triage' : '/google-watchdog')), [history, navState, actionSteps]);
  const toggleClaim = () => {
    if (!item || !['open', 'claimed'].includes(item.status)) return;
    service.claim(item.item_id, item.status === 'claimed').then(load).catch(e => setToast(e.message));
  };
  const isSnoozed = !!item && ['open', 'claimed'].includes(item.status) && parseTs(item.surface_on) > new Date();
  const reopenable = !!item && (item.status === 'dismissed' || item.status === 'done' || isSnoozed);
  const reopen = () => {
    if (!item) return;
    service.reopen(item.item_id).then(load).then(() => setToast('Reopened')).catch(e => setToast(e.message));
  };
  useKeymap({
    Escape: back,
    c: toggleClaim,
    d: () => { if (item) setDialog('resolve'); },
    x: () => { if (item) setDialog('dismiss'); },
    s: () => { if (item) setDialog('snooze'); },
    r: () => { if (reopenable) reopen(); },
  }, dialog === null && !stepDialogOpen);

  if (error && !item) return <Alert severity="error" sx={{ m: 4 }} action={<Button onClick={back}>Back</Button>}>{error}</Alert>;
  if (!item) return <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress size={24} /></Box>;

  const now = new Date();
  const actionable = ['open', 'claimed'].includes(item.status);
  const issues = item.card?.issues ?? [];
  const hasIssues = issues.length > 0;
  const isCampaign = Boolean(item.campaign_id || item.campaign_name);
  const linkedChange = changes.find(change => change.change_key === item.linked_change_key) ?? null;

  return (
    <Box sx={{ pb: 6 }}>
      <DetailHeader
        item={item} now={now} onBack={back}
        actionable={actionable} reopenable={reopenable}
        onClaim={toggleClaim} onResolve={() => setDialog('resolve')}
        onDismiss={() => setDialog('dismiss')} onSnooze={() => setDialog('snooze')} onReopen={reopen}
      />
      {toast && <Alert severity="warning" onClose={() => setToast('')} sx={{ m: 2 }}>{toast}</Alert>}
      <Box sx={{
        maxWidth: 1360, mx: 'auto', px: 3, mt: 2,
        display: 'grid', gap: 4,
        gridTemplateColumns: 'minmax(0, 1fr) 340px',
        '@media (max-width: 1100px)': { gridTemplateColumns: 'minmax(0, 1fr)' },
      }}>
        <Box sx={{ minWidth: 0 }}>
          {/* The agent's proposed changes come first on the triage card: they are the decision this
              page is opened to make, and the evidence below is there to check them against. */}
          {actionSteps && ACTION_STEP_STORES.includes(item.store ?? '') && <ActionSteps store={item.store as string} itemId={item.item_id} onDialogChange={setStepDialogOpen} />}
          {/* Move 1: what this campaign earns per day and per month, what the issues below are
              estimated to cost, and the three ratios behind those figures. Six labelled numbers,
              no sentence: a card can carry a red CRITICAL dot over a campaign that is beating
              its break-even, and a sentence has to pick one of those facts to lead with and bury
              the other, which is usually the one being looked for (L, 2026-08-07). */}
          {item.card?.economics && hasIssues ? (
            <FactStrip item={item} />
          ) : item.card?.economics?.bottomLine ? (
            <Typography component="h2" sx={{ maxWidth: '52ch', fontSize: '1.3125rem', fontWeight: 700, lineHeight: 1.35 }}>{item.card.economics.bottomLine}</Typography>
          ) : !hasIssues && item.headline ? (
            <Typography component="h2" sx={{ maxWidth: '52ch', fontSize: '1.3125rem', fontWeight: 700, lineHeight: 1.35 }}>{item.headline}</Typography>
          ) : null}

          {/* A change already on file that raised this item belongs beside the verdict, not
              buried in the rail's 30-day history, so nobody redoes work that is already done. */}
          {linkedChange && (
            <Typography variant="caption" sx={{ display: 'block', mt: 1, color: 'text.secondary' }}>
              Already changed on {localDay(linkedChange.changed_at)}{linkedChange.actor ? ` by ${linkedChange.actor}` : ''}: {linkedChange.summary}
            </Typography>
          )}

          {item.card?.lastDetected && <Alert severity="info" sx={{ mt: 2 }}>No longer detected since {item.card.lastDetected}.</Alert>}
          {item.card?.guardrailHeader && <Alert severity="warning" sx={{ mt: 2 }}>{item.card.guardrailHeader}</Alert>}

          {/* Return over time comes before the problems, because it is the first measure of
              whether anything is wrong at all: return can be healthy and the campaign can still
              have issues, but the issues are read against it (L, 2026-08-03). The arithmetic
              behind the return sits with the chart rather than with the issues, since it
              decomposes the whole campaign and not any one finding. */}
          <Box sx={{ mt: 3 }}>
            <TrendCharts
              series={item.card?.series}
              band={item.card?.band}
              breakeven={item.card?.roasStack?.breakeven ?? item.breakeven_roas ?? null}
              target={item.card?.roasStack?.googleTarget ?? item.target_roas ?? null}
              store={item.store}
            />
            <Waterfall id="decomposition" decomposition={item.card?.decomposition} issues={issues} spend7d={item.card?.funnelChain?.spend7d ?? null} store={item.store} />
          </Box>

          {/* Move 2: each problem, with its own instruction and its own checks. The card used
              to gather every instruction into one panel below the issues, which broke the link
              between a finding and the thing to do about it (L, 2026-08-03). */}
          {hasIssues ? (
            <>
              <SectionHeading count={issues.length}>What we found</SectionHeading>
              {issues.map((issue, index) => (
                <IssueSection key={issue.key} issue={issue} index={index} total={issues.length} fallback={itemCurrency(item)} />
              ))}
            </>
          ) : (
            <Box sx={{ mt: 3 }}><LegacyCardContent item={item} /></Box>
          )}

          {/* Move 3: everything that ran, not only what fired. Only campaign cards have
              coverage. A non-campaign card used to get a link to a standalone explainer page
              instead; that page is gone, and its content now lives inside this section where
              the outcomes it explains are. */}
          {isCampaign && (
            coverage === undefined
              ? <Typography variant="caption" sx={{ display: 'block', mt: 3, color: 'text.secondary' }}>Checking what ran…</Typography>
              : coverage != null && coverage.checks.length > 0 && <CoverageReceipt coverage={coverage} />
          )}

          {hasIssues && item.card?.briefHistory && item.card.briefHistory.length > 0 && (
            <BriefHistory history={item.card.briefHistory} />
          )}

          {item.status === 'done' && (
            <Typography variant="body2" sx={{ mt: 2, color: 'text.secondary' }}>Resolved by {item.resolved_by}{item.resolved_at && ` on ${localDay(item.resolved_at)}`}: {item.resolution_note}</Typography>
          )}
          {item.status === 'dismissed' && (
            <Typography variant="body2" sx={{ mt: 2, color: 'text.secondary' }}>Dismissed by {item.resolved_by}{item.resolved_at && ` on ${localDay(item.resolved_at)}`}{item.dismiss_reason && `: ${item.dismiss_reason}`}</Typography>
          )}
          <CaseHistory record={caseRecord} currentItemId={item.item_id} />
        </Box>

        <Box sx={{ minWidth: 0 }}>
          <FactsRail item={item} now={now} changes={changes} />
        </Box>
      </Box>

      <ResolveDialog open={dialog === 'resolve'} onClose={() => setDialog(null)} headline={item.headline ?? item.item_id}
        onResolve={async (note, kind) => { await service.resolve(item.item_id, note, kind); back(); }} />
      <DismissDialog open={dialog === 'dismiss'} onClose={() => setDialog(null)} headline={item.headline ?? item.item_id}
        onDismiss={async reason => { await service.dismiss(item.item_id, reason); back(); }} />
      <SnoozeDialog open={dialog === 'snooze'} onClose={() => setDialog(null)} headline={item.headline ?? item.item_id}
        onSnooze={async until => { await service.snooze(item.item_id, until); back(); }} />
    </Box>
  );
}

/**
 * One problem on the card. The boundary between two problems is the heaviest line in this
 * region and everything nested inside a problem is lighter than it. That was inverted before:
 * issues were split by the same 1px hairline used inside them while the "what to do" panel
 * carried a border AND a fill AND bold text, so four issues read as one block of prose
 * (L: "it kind of just looks like one giant wall and it doesn't help the eyes scan well").
 * No box around the issue itself — each one already contains a shaded panel, and DESIGN.md
 * bans nested containers.
 */
function IssueSection({ issue, index, total, fallback }: { issue: CardIssue; index: number; total: number; fallback: string }) {
  const [open, setOpen] = useState(false);
  const hasDetail = issue.detections.length > 0 || !!issue.brief;
  return (
    <Box sx={{ borderTop: '2px solid #CBD5E1', pt: 2.5, pb: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, minWidth: 0 }}>
        {/* A lone "1" on a single-issue card is noise: the ordinal only means something
            when there is a second issue to be ahead of. */}
        {total > 1 && (
          <Typography sx={{ fontSize: '1rem', fontWeight: 600, color: 'text.secondary', flexShrink: 0, fontVariantNumeric: 'tabular-nums' }}>
            {index + 1}
          </Typography>
        )}
        <Typography sx={{ fontSize: '1rem', fontWeight: 700, minWidth: 0, overflowWrap: 'anywhere' }}>{issue.title}</Typography>
        {issue.dollars != null && <Typography sx={{ ml: 'auto', pl: 2, flexShrink: 0, fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>{formatImpact(issue.dollars, issue.currency ?? fallback)}</Typography>}
      </Box>
      {/* The where-phrase gets its own line. Sharing the title's baseline made two different
          things at two different weights compete for one row, and it truncated first. */}
      {issue.where && (
        <Typography variant="caption" sx={{ display: 'block', mt: 0.25, color: 'text.secondary', overflowWrap: 'anywhere' }}>{issue.where}</Typography>
      )}
      <AdsLinks issue={issue} />
      {/* One or the other. A table of named rows already carries the metric that tripped, as its
          last column, so showing the single-metric row above it prints the lead finding twice. */}
      {issue.table
        ? <EntityTable table={issue.table} />
        : issue.metric && <MetricTable metric={issue.metric} />}
      {issue.whatWeSee && (
        <Box sx={{ mt: 1.25, maxWidth: '72ch' }}>
          <Typography variant="overline" sx={{ color: 'text.secondary' }}>What we see</Typography>
          <Typography variant="body2">{issue.whatWeSee}</Typography>
        </Box>
      )}
      {/* The instruction sits with the problem it belongs to. Gathering every instruction into
          one panel below the issues left the reader to pair them by eye, and on a card where two
          issues want opposite moves that pairing is the whole decision (L, 2026-08-03). */}
      {issue.whatToDo && (
        <Box sx={{ mt: 1.5, maxWidth: '72ch', p: 1.5, bgcolor: '#F8FAFC', borderRadius: 1 }}>
          <Typography variant="overline" sx={{ color: 'text.secondary' }}>What to do</Typography>
          <Typography variant="body2" sx={{ fontWeight: 600 }}>{issue.whatToDo}</Typography>
        </Box>
      )}
      {issue.foundBy && issue.foundBy.length > 0 && <FoundBy checks={issue.foundBy} />}
      {hasDetail && (
        <Button
          size="small"
          aria-expanded={open}
          aria-controls={`issue-detail-${issue.key}`}
          onClick={() => setOpen(value => !value)}
          sx={{ mt: 1, px: 0 }}
        >
          {open ? 'Hide more detail' : 'More detail'}
        </Button>
      )}
      {/* Flush left, no stripe. The button sits at px: 0, so an indented panel shifted the
          content 16px right of the control that revealed it (L: "a weird tab thing"). */}
      {hasDetail && open && (
        <Box id={`issue-detail-${issue.key}`} sx={{ mt: 1, maxWidth: '72ch' }}>
          {issue.detections.map((finding, index) => (
            <Box key={`${issue.key}-detection-${index}`} sx={{ display: 'flex', alignItems: 'baseline', gap: 1, mb: 0.5 }}>
              <Box aria-hidden="true" sx={{ width: 7, height: 7, borderRadius: '50%', flexShrink: 0, bgcolor: SEVERITY_COLORS[finding.severity], position: 'relative', top: -1 }} />
              {finding.severity === 'critical' && <Typography variant="caption" sx={{ color: '#DC2626', fontWeight: 700, letterSpacing: '0.04em' }}>CRITICAL</Typography>}
              <Typography variant="body2"><FindingText text={finding.text} />{finding.isNew && <Box component="span" sx={{ ml: 0.5, color: 'text.secondary' }}>(new)</Box>}</Typography>
            </Box>
          ))}
          {issue.brief && <BriefSection brief={issue.brief} />}
        </Box>
      )}
    </Box>
  );
}

const METRIC_TONE: Record<'bad' | 'neutral' | 'good', string> = {
  bad: '#DC2626', neutral: '#0F172A', good: '#047857',
};

/**
 * The number that tripped, as a labelled row rather than a large coloured figure. A 22px red
 * "3" said only that something was three, in a colour that said it was bad; the label and the
 * comparison it was three against sat beside it in caption grey, at a third of the weight of
 * the thing they explained (L: "a giant red 3 doesn't communicate the metric that changed and
 * why it's serious"). Headers carry the metric's own comparison word, so "usual" and "goal"
 * name the column instead of trailing the number.
 */
function MetricTable({ metric }: { metric: HeroMetric }) {
  const split = metric.baselineValue != null && metric.baselineLabel != null;
  const compareHeader = split ? metric.baselineLabel : 'Compared with';
  const compareCell = split ? metric.baselineValue : metric.baseline;
  const head = { textAlign: 'left' as const, color: 'text.secondary', fontSize: 11, fontWeight: 600, pb: 0.5, pr: 2.5, whiteSpace: 'nowrap' as const };
  const cell = { fontSize: 13.5, fontWeight: 600, pr: 2.5, fontVariantNumeric: 'tabular-nums' as const, whiteSpace: 'nowrap' as const };
  return (
    <Box component="table" sx={{ mt: 1.25, borderCollapse: 'collapse' }}>
      <Box component="thead">
        <Box component="tr">
          <Box component="th" sx={head}>Metric</Box>
          {/* "Now" is only right for a figure measured today. Where the finding averaged a
              stretch, the header says so, because that was the one thing the sentence carried
              that a column called Now silently drops. */}
          <Box component="th" sx={head}>{metric.window ?? 'Now'}</Box>
          {compareCell && <Box component="th" sx={{ ...head, textTransform: 'capitalize' }}>{compareHeader}</Box>}
          {metric.change && <Box component="th" sx={head}>Change</Box>}
        </Box>
      </Box>
      <Box component="tbody">
        <Box component="tr">
          <Box component="td" sx={{ ...cell, fontWeight: 400 }}>{metric.label}</Box>
          <Box component="td" sx={{ ...cell, color: METRIC_TONE[metric.tone] }}>{metric.value}</Box>
          {compareCell && <Box component="td" sx={{ ...cell, fontWeight: 400, color: 'text.secondary' }}>{compareCell}</Box>}
          {metric.change && <Box component="td" sx={{ ...cell, color: METRIC_TONE[metric.tone] }}>{metric.change}</Box>}
        </Box>
      </Box>
    </Box>
  );
}

/**
 * The issue's findings as rows. Three flagged keywords used to arrive as three sentences built to
 * one template, so comparing them meant reading the same shape three times and holding two numbers
 * per sentence in your head. As rows they are one glance down a column (L, 2026-08-04). Each
 * subject links to its own place in Google Ads, so the row is also the way in.
 */
function EntityTable({ table }: { table: IssueTable }) {
  const head = { textAlign: 'left' as const, color: 'text.secondary', fontSize: 11, fontWeight: 600, pb: 0.5, whiteSpace: 'nowrap' as const };
  const numHead = { ...head, textAlign: 'right' as const, pl: 2.5 };
  const cell = { fontSize: 13, py: 0.5, borderTop: '1px solid #F1F5F9' };
  const numCell = { ...cell, textAlign: 'right' as const, pl: 2.5, fontWeight: 600, fontVariantNumeric: 'tabular-nums' as const, whiteSpace: 'nowrap' as const };
  return (
    <Box sx={{ mt: 1.25, overflowX: 'auto' }}>
      <Box component="table" sx={{ borderCollapse: 'collapse', minWidth: '100%' }}>
        <Box component="thead">
          <Box component="tr">
            <Box component="th" sx={head}>{table.subjectLabel}</Box>
            {table.columns.map(column => <Box key={column} component="th" sx={numHead}>{column}</Box>)}
          </Box>
        </Box>
        <Box component="tbody">
          {table.rows.map(row => (
            <Box component="tr" key={row.subject}>
              <Box component="td" sx={{ ...cell, pr: 2, overflowWrap: 'anywhere' }}>
                {row.link
                  ? <Box component="a" href={row.link} target="_blank" rel="noopener noreferrer" sx={{ color: '#4F46E5', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}>{row.subject}</Box>
                  : row.subject}
              </Box>
              {row.cells.map((value, index) => (
                <Box key={table.columns[index]} component="td" sx={{ ...numCell, color: value == null ? 'text.secondary' : 'text.primary' }}>
                  {value ?? '-'}
                </Box>
              ))}
            </Box>
          ))}
        </Box>
      </Box>
    </Box>
  );
}

// Google Ads screens named by the path they open, so a button can say where it goes without
// borrowing the whole sentence its link was written into. An unrecognised Ads path still gets a
// button; it just says Google Ads.
const ADS_SCREENS: [string, string][] = [
  ['/aw/keywords/searchterms', 'Search terms'],
  ['/aw/keywords/negative', 'Negative keywords'],
  ['/aw/keywords', 'Keywords'],
  ['/aw/adgroups', 'Ad groups'],
  ['/aw/assetgroups', 'Asset groups'],
  ['/aw/ads', 'Ads'],
  ['/aw/audiences', 'Audiences'],
  ['/aw/campaigns', 'Campaigns'],
  ['/aw/overview', 'Overview'],
];

function adsScreen(url: string): string | null {
  if (!url.includes('ads.google.com')) return null;
  const path = url.split('?')[0];
  const hit = ADS_SCREENS.find(([prefix]) => path.includes(prefix));
  return hit ? hit[1] : 'Google Ads';
}

const SLACK_LINK = /<(https?:\/\/[^|>\s]+)(?:\|([^>]*))?>/g;

/**
 * The Google Ads destinations an issue's evidence already points at, raised to the top of the
 * issue. They were never dropped, but they have only ever existed inside the "More detail"
 * disclosure, written into the middle of an evidence sentence. So the one move an operator makes
 * on almost every card, open Google Ads at the thing being described, sat two clicks behind a
 * button labelled for reading (L, 2026-08-03).
 *
 * A link written about one keyword or search term names it in quotes, and that name is a better
 * button than the screen it opens: two links to the Keywords screen are two different keywords,
 * and labelling them "Keywords 1" and "Keywords 2" throws away the only thing that told them
 * apart. So the quoted subject wins when there is one, the screen name carries the rest, and
 * numbering is the last resort for genuine collisions. The sentence each link came from stays in
 * the evidence below and rides along as the button's tooltip.
 */
function AdsLinks({ issue }: { issue: CardIssue }) {
  const links = useMemo(() => {
    const seen = new Set<string>();
    const found: { url: string; base: string; hint: string }[] = [];
    issue.detections.forEach(detection => {
      const re = new RegExp(SLACK_LINK.source, 'g');
      let match = re.exec(detection.text);
      while (match !== null) {
        const url = match[1];
        const screen = adsScreen(url);
        if (screen && !seen.has(url)) {
          seen.add(url);
          const hint = match[2] || detection.text;
          const quoted = /"([^"]{1,40})"/.exec(hint);
          found.push({ url, base: quoted ? quoted[1] : screen, hint });
        }
        match = re.exec(detection.text);
      }
    });
    const counts = new Map<string, number>();
    found.forEach(link => counts.set(link.base, (counts.get(link.base) ?? 0) + 1));
    const used = new Map<string, number>();
    return found.map(link => {
      if ((counts.get(link.base) ?? 0) === 1) return { ...link, label: link.base };
      const nth = (used.get(link.base) ?? 0) + 1;
      used.set(link.base, nth);
      return { ...link, label: `${link.base} ${nth}` };
    });
  }, [issue.detections]);

  if (links.length === 0) return null;
  return (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mt: 1.25 }}>
      {links.map(link => (
        <Button key={link.url} size="small" variant="outlined" href={link.url} target="_blank" rel="noopener noreferrer"
          title={link.hint} endIcon={<OpenInNewIcon sx={{ fontSize: 14 }} />}
          sx={{ py: 0.125, px: 1, fontSize: 12, textTransform: 'none', borderColor: '#CBD5E1', color: 'text.primary' }}>
          {link.label}
        </Button>
      ))}
    </Box>
  );
}

/**
 * Who raised this problem, and what they refuse to claim. A bare check name was worse than
 * nothing: the five names this card used to print existed only in this file, so there was
 * nowhere to look them up, and the deck's real specialist names are no better on their own
 * (L: "I don't know what specialists ran, and I don't know the full checklist they went
 * through, and even then I haven't memorized every checklist item").
 *
 * So each check carries its own answer inline: what it watches for, the one rule that keeps
 * it honest, the claim it will not make, and how big its checklist is. The deck link is for
 * reading that checklist in full, not for finding out what the name means.
 */
function FoundBy({ checks }: { checks: WatchdogCheckDefinition[] }) {
  const [open, setOpen] = useState(false);
  return (
    <Box sx={{ mt: 1.5, maxWidth: '72ch' }}>
      <Box sx={{ display: 'flex', alignItems: 'baseline', flexWrap: 'wrap', gap: 0.75 }}>
        <Typography variant="caption" sx={{ color: 'text.secondary' }}>Raised by</Typography>
        <Typography variant="caption" sx={{ fontWeight: 600 }}>{checks.map(check => check.check).join(', ')}</Typography>
        <Button size="small" aria-expanded={open} onClick={() => setOpen(value => !value)} sx={{ minWidth: 0, px: 0.5 }}>
          {open ? 'Hide' : 'What that means'}
        </Button>
      </Box>
      {open && (
        <Box sx={{ mt: 0.5 }}>
          {checks.map(check => (
            <Box key={check.check} sx={{ mt: 1 }}>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>{check.check}</Typography>
              <Typography variant="caption" sx={{ display: 'block' }}>Watches for {check.watchesFor}.</Typography>
              {check.guardrail && <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary' }}>Will not fire unless {check.guardrail}.</Typography>}
              {check.wontClaim && <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary' }}>Does not claim {check.wontClaim}.</Typography>}
              {check.checklistSummary && (
                <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary' }}>
                  {check.checklistSummary}{' '}
                  <Box component="a" href={deckHref(check.deckSlide)} target="_blank" rel="noopener noreferrer" sx={{ color: '#6366F1' }}>Read the checklist</Box>
                </Typography>
              )}
            </Box>
          ))}
        </Box>
      )}
    </Box>
  );
}

function LegacyCardContent({ item }: { item: ActionItem }) {
  if (!item.card) return null;
  return (
    <>
      {item.card.detailLines && item.card.detailLines.length > 0 && (
        <Box sx={{ mb: 2, maxWidth: '72ch' }}>
          {item.card.detailLines.map((line, index) => <Typography key={index} variant="body2" sx={{ mt: index > 0 ? 1 : 0, color: 'text.secondary' }}>{line}</Typography>)}
        </Box>
      )}
      {item.card.brief && <BriefSection brief={item.card.brief} history={item.card.briefHistory} />}
      {item.card.children && item.card.children.length > 0 && (
        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>Evidence ({item.card.children.length})</Typography>
          {item.card.children.map((child, index) => <ChildBlock key={index} child={child} collapsible={item.card!.children!.length > 2} fallback={itemCurrency(item)} />)}
          {item.card.contextLine && <Typography variant="caption" sx={{ color: 'text.secondary' }}>{item.card.contextLine}</Typography>}
        </Box>
      )}
      {!item.card.children?.length && item.card.findings.length > 0 && (
        <Box sx={{ mb: 2 }}>
          {item.card.findings.map((finding, index) => (
            <Box key={index} sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
              <Box aria-hidden="true" sx={{ width: 8, height: 8, borderRadius: '50%', flexShrink: 0, bgcolor: SEVERITY_COLORS[finding.severity], position: 'relative', top: -1 }} />
              {finding.severity === 'critical' && <Typography variant="caption" sx={{ color: '#DC2626', fontWeight: 700, letterSpacing: '0.04em' }}>CRITICAL</Typography>}
              <Typography variant="body2"><FindingText text={finding.text} />{finding.isNew && <Box component="span" sx={{ ml: 0.5, color: 'text.secondary' }}>(new)</Box>}</Typography>
            </Box>
          ))}
        </Box>
      )}
    </>
  );
}

function ChildBlock({ child, collapsible, fallback }: { child: CardChild; collapsible: boolean; fallback: string }) {
  const [open, setOpen] = useState(!collapsible);
  return (
    <Box sx={{ border: '1px solid #E2E8F0', borderRadius: 1, p: 1.5, mb: 1 }}>
      <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, minWidth: 0 }}>
        <Typography variant="body2" sx={{ fontWeight: 600, minWidth: 0, overflowWrap: 'anywhere' }}>{child.label}</Typography>
        <Box sx={{ flex: 1 }} />
        {child.magnitude != null && <Typography variant="body2" sx={{ fontWeight: 700, fontVariantNumeric: 'tabular-nums', flexShrink: 0 }}>{formatImpact(child.magnitude * WEEKS_PER_MONTH, child.currency ?? fallback)}</Typography>}
      </Box>
      {child.headline !== child.label && <Typography variant="body2" sx={{ color: 'text.secondary', mb: 0.5 }}>{child.headline}</Typography>}
      {child.guardrailHeader && <Alert severity="warning" sx={{ my: 1 }}>{child.guardrailHeader}</Alert>}
      {child.findings.map((finding, index) => (
        <Box key={index} sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
          <Box aria-hidden="true" sx={{ width: 8, height: 8, borderRadius: '50%', flexShrink: 0, bgcolor: SEVERITY_COLORS[finding.severity], position: 'relative', top: -1 }} />
          {finding.severity === 'critical' && <Typography variant="caption" sx={{ color: '#DC2626', fontWeight: 700, letterSpacing: '0.04em' }}>CRITICAL</Typography>}
          <Typography variant="body2"><FindingText text={finding.text} />{finding.isNew && <Box component="span" sx={{ ml: 0.5, color: 'text.secondary' }}>(new)</Box>}</Typography>
        </Box>
      ))}
      {child.brief && (open ? <BriefSection brief={child.brief} /> : <Button size="small" sx={{ mt: 0.5, px: 0 }} onClick={() => setOpen(true)}>Show diagnosis</Button>)}
    </Box>
  );
}

function BriefSection({ brief, history }: { brief: CardBrief; history?: CardBrief[] }) {
  const [showHistory, setShowHistory] = useState(false);
  return (
    <Box sx={{ mb: 2, p: 2, bgcolor: '#F8FAFC', borderRadius: 1 }}>
      <Typography variant="subtitle2">Diagnosis{brief.basis && <Typography component="span" variant="caption" sx={{ color: 'text.secondary' }}> ({brief.basis})</Typography>}</Typography>
      {brief.explanation && <Typography variant="body2" sx={{ mt: 1 }}>{brief.explanation}</Typography>}
      {brief.diagnoses.length > 0 ? (
        <Box component="ol" sx={{ pl: 3, my: 1 }}>
          {brief.diagnoses.map(d => <li key={d.rank}><Typography variant="body2">{d.cause}: {d.action} <Typography component="span" variant="caption" sx={{ color: 'text.secondary' }}>({d.basis})</Typography>{d.caveat && <Typography component="span" variant="caption" sx={{ color: 'warning.main' }}> ⚠ {d.caveat}</Typography>}</Typography></li>)}
        </Box>
      ) : (
        <>
          {brief.cause && <Typography variant="body2" sx={{ mt: 1 }}><b>Cause:</b> {brief.cause}</Typography>}
          {brief.contributing.length > 0 && <Typography variant="body2"><b>Contributing:</b> {brief.contributing.join(', ')}</Typography>}
          {brief.recommended && <Typography variant="body2"><b>Recommended:</b> {brief.recommended}</Typography>}
        </>
      )}
      {brief.evidenceSummary && <Typography variant="body2" sx={{ mt: 1 }}><b>Evidence:</b> {brief.evidenceSummary}</Typography>}
      {brief.beyondTheData.length > 0 && (
        <Box sx={{ mt: 2 }}>
          <Typography variant="subtitle2">Beyond the data</Typography>
          {brief.beyondTheData.map((b, index) => <Box key={index} sx={{ mt: 1 }}><Typography variant="body2" sx={{ fontWeight: 600 }}>{b.hypothesis}</Typography><Typography variant="body2" sx={{ color: 'text.secondary' }}>{b.why}</Typography></Box>)}
        </Box>
      )}
      <Typography variant="caption" sx={{ display: 'block', mt: 1, color: 'text.secondary' }}>Synthesized {brief.synthesizedAt}</Typography>
      {history && history.length > 0 && (
        <Box sx={{ mt: 1 }}>
          <Button size="small" onClick={() => setShowHistory(value => !value)}>{showHistory ? 'Hide' : 'Show'} previous diagnoses ({history.length})</Button>
          {showHistory && history.slice().reverse().map((entry, index) => <Box key={index} sx={{ mt: 1, pl: 2, borderLeft: '2px solid', borderColor: 'divider' }}><Typography variant="caption" display="block">{entry.synthesizedAt}: {entry.cause} ({entry.basis})</Typography><Typography variant="caption" display="block" sx={{ color: 'text.secondary' }}>{entry.explanation}</Typography></Box>)}
        </Box>
      )}
    </Box>
  );
}

function BriefHistory({ history }: { history: CardBrief[] }) {
  return (
    <Box sx={{ mb: 3 }}>
      <Typography variant="overline" sx={{ color: 'text.secondary' }}>Brief history</Typography>
      {history.slice().reverse().map((entry, index) => (
        <Box key={index} sx={{ mt: 1, pl: 2, borderLeft: '2px solid', borderColor: 'divider' }}>
          <Typography variant="caption" display="block">{entry.synthesizedAt}: {entry.cause} ({entry.basis})</Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>{entry.explanation}</Typography>
        </Box>
      ))}
    </Box>
  );
}

type CoverageDisplayOutcome = CoverageOutcome | 'not_checked';
const COVERAGE_DOT: Record<CoverageDisplayOutcome, { bg: string; border: string }> = {
  clean: { bg: '#10B981', border: '#10B981' },
  flagged: { bg: '#DC2626', border: '#DC2626' },
  insufficient_data: { bg: 'transparent', border: '#D97706' },
  not_applicable: { bg: 'transparent', border: '#94A3B8' },
  skipped: { bg: 'transparent', border: '#CBD5E1' },
  not_checked: { bg: 'transparent', border: '#CBD5E1' },
};
const COVERAGE_WORD: Record<CoverageDisplayOutcome, string> = {
  clean: 'clean', flagged: 'flagged', insufficient_data: 'insufficient data',
  not_applicable: 'not applicable', skipped: 'skipped', not_checked: 'not checked',
};

function formatRunDate(runDate: string): string {
  const date = new Date(runDate.length === 10 ? `${runDate}T00:00:00Z` : runDate);
  return date.toLocaleDateString('en-US', { month: 'long', day: 'numeric', timeZone: 'UTC' });
}

const LAYER_LABEL: Record<'detection' | 'diagnosis', string> = {
  detection: 'Watching every campaign',
  diagnosis: 'Working out why',
};
const LAYER_BLURB: Record<'detection' | 'diagnosis', string> = {
  detection: 'These run on every campaign every time, whether or not anything looks wrong. They are what decides a campaign is worth a closer look.',
  diagnosis: 'These are the ten specialists. Each one owns a step of the funnel, works through a fixed checklist in a fixed order, and stops at what its evidence can carry.',
};

/**
 * What we checked: every check the run performed on this campaign, closed by default. A
 * reader who cannot see what was searched for has no reason to believe "clear", so every
 * check names what it watches for beside what it found, and a check that did not run says
 * why rather than being dropped from the list.
 *
 * Grouped by layer, because the two layers answer different questions and mixing them made
 * every name look equally arbitrary. The summary line counts outcomes instead of claiming a
 * number of checks "ran" — the old wording counted anything that was not `not_checked`,
 * which quietly promoted skipped and not-applicable checks into work that had happened.
 */
function CoverageReceipt({ coverage }: { coverage: WatchdogCoverage }) {
  const [open, setOpen] = useState(false);
  const checks = coverage.checks;
  const flagged = checks.filter(check => check.outcome === 'flagged').length;
  const clean = checks.filter(check => check.outcome === 'clean').length;
  const stalled = checks.length - flagged - clean;
  if (!coverage.runDate) return null;
  const summary = [
    flagged > 0 ? `${flagged} flagged` : null,
    clean > 0 ? `${clean} clear` : null,
    stalled > 0 ? `${stalled} could not run` : null,
  ].filter(Boolean).join(', ');
  const layers: Array<'detection' | 'diagnosis'> = ['detection', 'diagnosis'];
  return (
    <>
      <SectionHeading count={checks.length}>What we checked</SectionHeading>
      <Box sx={{ maxWidth: '72ch' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 0.5 }}>
          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
            {formatRunDate(coverage.runDate)}: {summary}.
          </Typography>
          <Button size="small" aria-expanded={open} onClick={() => setOpen(value => !value)} sx={{ minWidth: 0, px: 0.5 }}>{open ? 'Hide' : 'Show every check'}</Button>
        </Box>
        {open && (
          <Box sx={{ mt: 1 }}>
            {layers.map(layer => {
              const group = checks.filter(check => check.layer === layer);
              if (group.length === 0) return null;
              return (
                <Box key={layer} sx={{ mt: 2 }}>
                  <Typography variant="body2" sx={{ fontWeight: 700 }}>{LAYER_LABEL[layer]} ({group.length})</Typography>
                  <Typography variant="caption" sx={{ display: 'block', mb: 0.5, color: 'text.secondary' }}>{LAYER_BLURB[layer]}</Typography>
                  {group.map(check => <CheckRow key={check.check} check={check} />)}
                </Box>
              );
            })}
            <Box sx={{ mt: 2, pt: 1.5, borderTop: '1px solid #E2E8F0' }}>
              <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary' }}>
                Fast checks read the last 7 days against the campaign's own recent history. Trend checks compare 30, 60, 90 and 180 days, so a slow slide cannot hide inside one good week.
              </Typography>
              <Typography variant="caption" sx={{ display: 'block', mt: 0.5, color: 'text.secondary' }}>
                An issue has to be worth roughly $200 a month, and at least 10 percent of the campaign's spend, before it reaches this queue.
              </Typography>
              <Typography variant="caption" sx={{ display: 'block', mt: 0.5, color: 'text.secondary' }}>
                Could not run means exactly that. Nothing here calls a check clear when it did not report.
              </Typography>
            </Box>
          </Box>
        )}
      </Box>
    </>
  );
}

/**
 * One check and what it reported. The name is never left to stand alone: the line under it
 * says what the check watches for, which is the part a reader can act on without having
 * memorized anything.
 */
function CheckRow({ check }: { check: WatchdogCheck }) {
  const dot = COVERAGE_DOT[check.outcome];
  return (
    <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, py: 0.5 }}>
      <Box aria-hidden="true" sx={{ width: 7, height: 7, flexShrink: 0, borderRadius: '50%', bgcolor: dot.bg, border: `1px solid ${dot.border}`, position: 'relative', top: -1 }} />
      <Typography variant="caption" sx={{ width: 200, flexShrink: 0, fontWeight: 600 }}>{check.check}</Typography>
      <Typography variant="caption" sx={{ width: 110, flexShrink: 0, color: 'text.secondary' }}>{COVERAGE_WORD[check.outcome]}</Typography>
      <Box sx={{ minWidth: 0 }}>
        <Typography variant="caption" sx={{ display: 'block' }}>
          Watches for {check.watchesFor}.
          {check.deckSlide && (
            <>
              {' '}
              <Box component="a" href={deckHref(check.deckSlide)} target="_blank" rel="noopener noreferrer" sx={{ color: '#6366F1' }}>Checklist</Box>
            </>
          )}
        </Typography>
        {check.detail && <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary' }}>{check.detail}</Typography>}
      </Box>
    </Box>
  );
}
